/**
 * WebSocket client for `/ws/jobs` (job progress pushes) with auto-reconnect + a jobs store.
 * The store merges pushed updates with jobs fetched from `GET /jobs`.
 */
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useMoment } from '@/module/MomentProvider';
import type { JobOut, JobPush, JobStatus } from './types';

export type JobRecord = JobPush & { created_at?: string; task_id?: string | null; finished_at?: string | null };

export interface JobsWsOptions {
  url: string;
  onMessage: (job: JobPush) => void;
  onStatus?: (status: 'connecting' | 'open' | 'closed') => void;
  minDelayMs?: number;
  maxDelayMs?: number;
}

/** Minimal reconnecting WebSocket. Sends a periodic ping so the server's `receive_text` loop keeps the socket alive. */
export class JobsWebSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private closed = false;
  constructor(private readonly opts: JobsWsOptions) {}

  connect(): void {
    if (this.closed) return;
    this.opts.onStatus?.('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.opts.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.opts.onStatus?.('open');
      this.ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send('ping');
      }, 25_000);
    };
    ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(String(ev.data)) as JobPush;
        if (data && typeof data === 'object' && 'id' in data) this.opts.onMessage(data);
      } catch {
        /* ignore non-JSON keepalives */
      }
    };
    ws.onclose = () => {
      if (this.ping) clearInterval(this.ping);
      this.ping = null;
      this.opts.onStatus?.('closed');
      this.scheduleReconnect();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleReconnect(): void {
    if (this.closed) return;
    const min = this.opts.minDelayMs ?? 1000;
    const max = this.opts.maxDelayMs ?? 30_000;
    const delay = Math.min(max, min * 2 ** this.attempt) + Math.random() * 500;
    this.attempt += 1;
    this.timer = setTimeout(() => this.connect(), delay);
  }

  close(): void {
    this.closed = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.ping) clearInterval(this.ping);
    this.ws?.close();
    this.ws = null;
  }
}

// ---------- store ----------
interface JobsStore {
  jobs: Record<string, JobRecord>;
  status: 'connecting' | 'open' | 'closed';
  upsert: (job: JobRecord) => void;
  dismiss: (id: string) => void;
  clearFinished: () => void;
}

const JobsContext = createContext<JobsStore | null>(null);

const ACTIVE: JobStatus[] = ['queued', 'running'];

export function JobsStoreProvider({ children }: { children: ReactNode }) {
  const { client, api } = useMoment();
  const qc = useQueryClient();
  const [jobs, setJobs] = useState<Record<string, JobRecord>>({});
  const [status, setStatus] = useState<'connecting' | 'open' | 'closed'>('closed');
  const invalidateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const upsert = useCallback((job: JobRecord) => {
    setJobs((prev) => ({ ...prev, [job.id]: { ...prev[job.id], ...job } }));
  }, []);

  // Refresh related queries when a job finishes (debounced: bulk analyses finish in bursts).
  const onFinished = useCallback(
    (job: JobPush) => {
      if (invalidateTimer.current) clearTimeout(invalidateTimer.current);
      invalidateTimer.current = setTimeout(() => {
        const keys: string[] = ['jobs'];
        if (job.type === 'analyze') keys.push('videos', 'video', 'sources', 'costs', 'moments');
        if (job.type === 'discover') keys.push('sources', 'videos');
        if (job.type === 'compute_segment') keys.push('segments', 'segment', 'segment-members');
        if (job.type === 'export') keys.push('exports');
        for (const k of keys) void qc.invalidateQueries({ queryKey: [k] });
      }, 400);
    },
    [qc],
  );

  useEffect(() => {
    // seed with currently active jobs
    let cancelled = false;
    void Promise.all([api.jobs.list({ status: 'running', limit: 100 }), api.jobs.list({ status: 'queued', limit: 100 })])
      .then(([running, queued]) => {
        if (cancelled) return;
        setJobs((prev) => {
          const next = { ...prev };
          for (const j of [...running.items, ...queued.items]) next[j.id] = { ...next[j.id], ...toRecord(j) };
          return next;
        });
      })
      .catch(() => undefined);

    const socket = new JobsWebSocket({
      url: client.wsUrl(),
      onStatus: setStatus,
      onMessage: (job) => {
        upsert(job);
        if (job.status === 'done' || job.status === 'failed') onFinished(job);
      },
    });
    socket.connect();
    return () => {
      cancelled = true;
      socket.close();
    };
  }, [client, api, upsert, onFinished]);

  const dismiss = useCallback((id: string) => {
    setJobs((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);
  const clearFinished = useCallback(() => {
    setJobs((prev) => Object.fromEntries(Object.entries(prev).filter(([, j]) => ACTIVE.includes(j.status))));
  }, []);

  const value = useMemo<JobsStore>(
    () => ({ jobs, status, upsert, dismiss, clearFinished }),
    [jobs, status, upsert, dismiss, clearFinished],
  );
  return createElement(JobsContext.Provider, { value }, children);
}

function toRecord(j: JobOut): JobRecord {
  return {
    id: j.id,
    type: j.type,
    ref_type: j.ref_type,
    ref_id: j.ref_id,
    status: j.status,
    progress: j.progress,
    message: j.message,
    error: j.error,
    updated_at: j.updated_at,
    created_at: j.created_at,
    task_id: j.task_id,
    finished_at: j.finished_at,
  };
}

/** Jobs merged from pushes + initial fetch, newest first. */
export function useJobs() {
  const ctx = useContext(JobsContext);
  if (!ctx) throw new Error('useJobs must be used inside <MomentProvider>');
  const list = useMemo(
    () => Object.values(ctx.jobs).sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? '')),
    [ctx.jobs],
  );
  const running = useMemo(() => list.filter((j) => ACTIVE.includes(j.status)), [list]);
  return { jobs: list, running, status: ctx.status, dismiss: ctx.dismiss, clearFinished: ctx.clearFinished, upsert: ctx.upsert };
}

/** Latest job for a given ref (e.g. a video id), if any. */
export function useJobForRef(refId: string | undefined) {
  const { jobs } = useJobs();
  return useMemo(() => (refId ? jobs.find((j) => j.ref_id === refId) : undefined), [jobs, refId]);
}
